import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFirebase } from '../../context/FirebaseContext';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import GoogleIcon from '@mui/icons-material/Google';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import './SignUp.css';
import { Link as RouterLink } from 'react-router-dom';
import { getFirebaseErrorMessage } from '../../utils/firebaseErrors';
import { getAlphaSystemStatus } from '../../api/alpha';
import logger from '../../utils/logger';
import { analytics } from '../../firebaseConfig';
import { logEvent } from 'firebase/analytics';

function SignUp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signInWithGoogle, signUpWithEmail } = useFirebase();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [alphaStatus, setAlphaStatus] = useState(null);
  const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Check alpha status and load invite code from URL
    const loadAlphaStatus = async () => {
      const status = await getAlphaSystemStatus();
      setAlphaStatus(status);
    };
    loadAlphaStatus();

    // If invite code in URL, store it for the login flow
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      setInviteCode(codeFromUrl);
      localStorage.setItem('pendingInviteCode', codeFromUrl);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }

    // Note: We no longer block if invite code is missing.
    // The backend handles this - if no code, user is created but has no alpha access,
    // and will be redirected to the waitlist page.

    try {
      setError('');
      setLoading(true);
      logEvent(analytics, 'signup_attempt', { method: 'email', has_invite_code: !!inviteCode });
      // Store invite code for the login flow in App.js
      if (inviteCode) {
        localStorage.setItem('pendingInviteCode', inviteCode);
      }
      await signUpWithEmail(formData.email, formData.password);
      logEvent(analytics, 'signup_success', { method: 'email', has_invite_code: !!inviteCode });
      navigate('/');
    } catch (err) {
      logger.error('Signup error:', err);
      logEvent(analytics, 'signup_failed', { method: 'email', error_code: err.code });
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    // Note: We no longer block if invite code is missing.
    // The backend handles this - if no code, user is created but has no alpha access,
    // and will be redirected to the waitlist page.

    setError('');
    setLoading(true);
    logEvent(analytics, 'signup_attempt', { method: 'google', has_invite_code: !!inviteCode });

    try {
      // Store invite code for the login flow in App.js
      if (inviteCode) {
        localStorage.setItem('pendingInviteCode', inviteCode);
      }
      await signInWithGoogle();
      logEvent(analytics, 'signup_success', { method: 'google', has_invite_code: !!inviteCode });
      navigate('/');
    } catch (err) {
      logger.error('Google sign up error:', err);
      logEvent(analytics, 'signup_failed', { method: 'google', error_code: err.code });
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const isInviteRequired = alphaStatus?.alphaEnabled && alphaStatus?.inviteRequired;
  const isSignupCapReached = alphaStatus?.signupCapReached === true;

  return (
    <Box className="ntb-copilot-signup-container">
      <Box className="ntb-copilot-signup-card">
        <Box className="ntb-copilot-signup-header">
          <Typography variant="h4" component="h1">
            Create Account
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Get started with NanoTradingCopilot
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isSignupCapReached && !inviteCode?.trim() && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Free spots are full &mdash; enter an invite code or join the waitlist.{' '}
            <Link to="/waitlist" component={RouterLink} sx={{ fontWeight: 500 }}>
              Join the waitlist
            </Link>
          </Alert>
        )}

        {isInviteRequired && !isSignupCapReached && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VpnKeyIcon fontSize="small" />
              <span>Invite code required for alpha access</span>
            </Box>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="ntb-copilot-signup-form">
          {(isInviteRequired || isSignupCapReached) && (
            <TextField
              fullWidth
              label="Invite Code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="ALPHA-XXXX-XXXX"
              variant="outlined"
              required={isInviteRequired}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKeyIcon color={inviteCode ? 'primary' : 'disabled'} />
                  </InputAdornment>
                ),
              }}
              helperText={inviteCode ? '' : "Don't have a code? Join the waitlist below."}
            />
          )}

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            variant="outlined"
            required
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            placeholder="Create a password"
            variant="outlined"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm your password"
            variant="outlined"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="textSecondary">
                I agree to the{' '}
                <Link to="/terms" component={RouterLink} target="_blank">Terms of Service</Link>,{' '}
                <Link to="/privacy" component={RouterLink} target="_blank">Privacy Policy</Link>, and{' '}
                <Link to="/risk-disclaimer" component={RouterLink} target="_blank">Risk Disclaimer</Link>
              </Typography>
            }
            sx={{ mt: 1, mb: 1, alignItems: 'flex-start' }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            className="ntb-copilot-create-account-btn"
            disabled={loading || !acceptedTerms || (isSignupCapReached && !inviteCode?.trim())}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>

          <Box className="ntb-copilot-divider">
            <span>Or</span>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            className="ntb-copilot-google-btn"
            onClick={handleGoogleSignUp}
            disabled={loading || !acceptedTerms || (isSignupCapReached && !inviteCode?.trim())}
          >
            Sign up with Google
          </Button>
        </form>

        <Box className="ntb-copilot-signin-link">
          <Typography variant="body2">
            Already have an account? <Link to="/login" component={RouterLink}>Sign in</Link>
          </Typography>
        </Box>

        {(isInviteRequired || isSignupCapReached) && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {isSignupCapReached
                ? 'Want to be notified when a spot opens? '
                : "Don't have an invite code? "}
              <Link to="/waitlist" component={RouterLink} sx={{ fontWeight: 500 }}>
                Join the waitlist
              </Link>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default SignUp; 